// ═══════════════════════════════════════════════════════════════════
//  modules/projets/TaskCard.js
//  TaskCard
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "../../composants/Badge";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";
import { CATEGORY_LABELS, PROJECT_PRIORITY_COLORS } from "./constantes";
import { TaskHighlightButton } from "./TaskHighlightButton";

export function TaskCard({ task, token, onUpdated }) {
  const prioInfo = PROJECT_PRIORITY_COLORS[task.priority] || PROJECT_PRIORITY_COLORS.moyenne;
  const [showPatchNote, setShowPatchNote] = useState(false);
  const [patchNoteText, setPatchNoteText] = useState("");
  const [extraText, setExtraText] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [showJournal, setShowJournal] = useState(false);
  const [journal, setJournal] = useState([]);
  const [journalInput, setJournalInput] = useState("");
  const [journalSending, setJournalSending] = useState(false);
  const journalContainerRef = useRef(null);
  const prevJournalCount = useRef(0);

  const generatePatchNoteAI = async () => {
    setAiGenerating(true);
    try {
      const res = await fetch(`${API_BASE}/api/project/tasks/${task.id}/patchnote-ai`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ extra_text: extraText }),
      });
      const data = await res.json();
      if (data.patch_note) setPatchNoteText(data.patch_note);
    } catch (err) { console.error("Erreur génération IA:", err); }
    setAiGenerating(false);
  };

  const doneSteps = task.steps.filter((s) => s.is_done).length;
  const totalSteps = task.steps.length;

  const loadJournal = useCallback(async () => {
    const res = await fetch(`${API_BASE}/api/project/tasks/${task.id}/journal`, { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    setJournal(Array.isArray(data) ? data : []);
  }, [task.id, token]);

  useEffect(() => {
    if (showJournal) {
      loadJournal();
      const interval = setInterval(loadJournal, 5000);
      return () => clearInterval(interval);
    }
  }, [showJournal, loadJournal]);

  useEffect(() => {
    if (journal.length > prevJournalCount.current && journalContainerRef.current) {
      journalContainerRef.current.scrollTop = journalContainerRef.current.scrollHeight;
    }
    prevJournalCount.current = journal.length;
  }, [journal, loadJournal]);

  const sendJournal = async () => {
    if (!journalInput.trim()) return;
    setJournalSending(true);
    await fetch(`${API_BASE}/api/project/tasks/${task.id}/journal`, {
      method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ content: journalInput }),
    });
    setJournalInput("");
    setJournalSending(false);
    loadJournal();
  };

  const claim = async () => {
    await fetch(`${API_BASE}/api/project/tasks/${task.id}/claim`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
    onUpdated();
  };


  const changeStatus = async (status) => {
    if (status === "termine" && !task.patch_note) { setShowPatchNote(true); return; }
    await fetch(`${API_BASE}/api/project/tasks/${task.id}/status`, {
      method: "PATCH", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status }),
    });
    onUpdated();
  };

  const toggleStep = async (stepId, isDone) => {
    await fetch(`${API_BASE}/api/project/steps/${stepId}`, {
      method: "PATCH", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ is_done: !isDone }),
    });
    onUpdated();
  };

  const submitPatchNote = async () => {
    await fetch(`${API_BASE}/api/project/tasks/${task.id}/patchnote`, {
      method: "PATCH", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ patch_note: patchNoteText }),
    });
    await fetch(`${API_BASE}/api/project/tasks/${task.id}/status`, {
      method: "PATCH", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status: "termine" }),
    });
    setShowPatchNote(false);
    onUpdated();
  };

  return (
    <div style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 8, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        {/* PASTILLE_CATEGORIE — les colonnes mélangent maintenant toutes les
            catégories : sans la pastille, on perd le seul repère qui disait
            de quel domaine relève la tâche. */}
        <div style={{ flex: 1 }}>
          {CATEGORY_LABELS[task.category] && (
            <div style={{ marginBottom: 5 }}>
              <Badge text={CATEGORY_LABELS[task.category].label}
                bg={CATEGORY_LABELS[task.category].bg}
                color={CATEGORY_LABELS[task.category].color} />
            </div>
          )}
          <div style={{ color: THEME.text, fontSize: 13, fontWeight: 600 }}>{task.title}</div>
        </div>
        <button onClick={async (e) => {
          e.stopPropagation();
          if (!window.confirm("Supprimer cette tâche ?")) return;
          await fetch(`${API_BASE}/api/project/tasks/${task.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
          onUpdated();
        }} style={{ background: "transparent", border: "none", color: THEME.textDim, cursor: "pointer", fontSize: 14, padding: "0 0 0 8px", flexShrink: 0 }} title="Supprimer la tâche">🗑️</button>
      </div>
      {task.description && <div style={{ color: THEME.textMuted, fontSize: 12 }}>{task.description}</div>}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <Badge text={task.priority} bg={prioInfo.bg} color={prioInfo.text} />
        <TaskHighlightButton taskId={task.id} token={token} />
      </div>
      {totalSteps > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ color: THEME.textMuted, fontSize: 11 }}>{doneSteps}/{totalSteps} étapes</div>
          {task.steps.map((s) => (
            <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input type="checkbox" checked={!!s.is_done} onChange={() => toggleStep(s.id, s.is_done)} />
              <span style={{ color: s.is_done ? THEME.textDim : THEME.text, fontSize: 12, textDecoration: s.is_done ? "line-through" : "none" }}>{s.label}</span>
            </label>
          ))}
        </div>
      )}
      {task.patch_note && (
        <div style={{ background: THEME.emeraldDim, border: `1px solid ${THEME.emerald}`, borderRadius: 6, padding: "8px 10px" }}>
          <div style={{ color: THEME.emerald, fontSize: 10, fontWeight: 700, marginBottom: 4 }}>📋 PATCH NOTE</div>
          <div style={{ color: THEME.text, fontSize: 12 }}>{task.patch_note}</div>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
        <span style={{ color: THEME.textMuted, fontSize: 11 }}>{task.assigned_username ? `👤 ${task.assigned_username}` : "Non assigné"}</span>
        {!task.assigned_to && task.status !== "termine" && (
          <button onClick={claim} style={{ background: THEME.accentDim, border: `1px solid ${THEME.accent}`, color: THEME.accent, borderRadius: 5, padding: "4px 10px", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>C'est moi qui fais</button>
        )}
      </div>
      {task.status !== "termine" && (
        <div style={{ display: "flex", gap: 6 }}>
          {task.status === "a_faire" && <button onClick={() => changeStatus("en_cours")} style={{ flex: 1, background: "transparent", border: `1px solid ${THEME.amber}`, color: THEME.amber, borderRadius: 5, padding: "5px", cursor: "pointer", fontSize: 11 }}>▶ Démarrer</button>}
          {task.status === "en_cours" && <button onClick={() => changeStatus("termine")} style={{ flex: 1, background: "transparent", border: `1px solid ${THEME.emerald}`, color: THEME.emerald, borderRadius: 5, padding: "5px", cursor: "pointer", fontSize: 11 }}>✓ Terminer</button>}
        </div>
      )}
      {task.assigned_to && (
        <button onClick={() => setShowJournal(!showJournal)}
          style={{ background: showJournal ? THEME.goldDim : "transparent", border: `1px solid ${THEME.gold}`, color: THEME.gold, borderRadius: 5, padding: "5px 10px", cursor: "pointer", fontSize: 11, fontWeight: 600, textAlign: "left" }}>
          📝 Journal de bord {journal.length > 0 && !showJournal ? `(${journal.length} entrées)` : showJournal ? "▲ Fermer" : "▼ Ouvrir"}
        </button>
      )}
      {showJournal && (
        <div style={{ background: THEME.panel, border: `1px solid ${THEME.gold}`, borderRadius: 8, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ color: THEME.gold, fontSize: 11, fontWeight: 700 }}>📝 Journal de bord — {task.assigned_username}</div>
          <div ref={journalContainerRef} style={{ maxHeight: 180, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
            {journal.length === 0 && <div style={{ color: THEME.textDim, fontSize: 12 }}>Aucune entrée pour l'instant.</div>}
            {journal.map((j) => (
              <div key={j.id} style={{ background: THEME.surface, borderRadius: 6, padding: "6px 10px" }}>
                <div style={{ color: THEME.gold, fontSize: 10, fontWeight: 700, marginBottom: 2 }}>{j.author_username} · {new Date(j.created_at).toLocaleString("fr", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</div>
                <div style={{ color: THEME.text, fontSize: 12 }}>{j.content}</div>
              </div>
            ))}
          </div>
          {task.status !== "termine" && (
            <div style={{ display: "flex", gap: 6 }}>
              <input value={journalInput} onChange={(e) => setJournalInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendJournal()} placeholder="Note d'avancement, blocage, info..."
                style={{ flex: 1, background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 6, padding: "6px 10px", color: THEME.text, fontSize: 12, outline: "none" }} />
              <button onClick={sendJournal} disabled={journalSending} style={{ background: THEME.goldDim, border: `1px solid ${THEME.gold}`, color: THEME.gold, borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>+</button>
            </div>
          )}
        </div>
      )}
      {showPatchNote && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, background: THEME.panel, borderRadius: 6, padding: 12 }}>
          <div style={{ color: THEME.gold, fontSize: 11, fontWeight: 700 }}>📋 Note de clôture (patch note)</div>
          <div style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 6, padding: 8 }}>
            <div style={{ color: THEME.textMuted, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>CONTEXTE SUPPLÉMENTAIRE (optionnel)</div>
            <textarea value={extraText} onChange={(e) => setExtraText(e.target.value)} rows={3} placeholder="Colle ici une conversation, des notes en vrac..."
              style={{ width: "100%", background: "transparent", border: "none", color: THEME.text, fontSize: 12, outline: "none", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          <button onClick={generatePatchNoteAI} disabled={aiGenerating}
            style={{ background: THEME.purpleDim, border: `1px solid ${THEME.purple}`, color: THEME.purple, borderRadius: 6, padding: "7px", cursor: aiGenerating ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, opacity: aiGenerating ? 0.6 : 1 }}>
            {aiGenerating ? "⏳ Génération en cours..." : "🤖 Générer avec l'IA (journal + contexte)"}
          </button>
          <div style={{ color: THEME.textMuted, fontSize: 10, fontWeight: 600 }}>PATCH NOTE FINAL (éditable)</div>
          <textarea value={patchNoteText} onChange={(e) => setPatchNoteText(e.target.value)} rows={5} placeholder="Le patch note apparaîtra ici après génération IA, ou écris-le manuellement..."
            style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 6, padding: 8, color: THEME.text, fontSize: 12, outline: "none", resize: "vertical", fontFamily: "inherit" }} />
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => { setShowPatchNote(false); setExtraText(""); setPatchNoteText(""); }}
              style={{ flex: 1, background: "transparent", border: `1px solid ${THEME.border}`, color: THEME.textMuted, borderRadius: 5, padding: "6px", cursor: "pointer", fontSize: 11 }}>Annuler</button>
            <button onClick={submitPatchNote} disabled={!patchNoteText.trim()}
              style={{ flex: 1, background: THEME.emeraldDim, border: `1px solid ${THEME.emerald}`, color: THEME.emerald, borderRadius: 5, padding: "6px", cursor: "pointer", fontSize: 11, fontWeight: 700, opacity: !patchNoteText.trim() ? 0.5 : 1 }}>Valider & Terminer</button>
          </div>
        </div>
      )}
    </div>
  );
}
